import { featureFlags } from 'common/featureFlags/featureFlags';
import { AuthUserType, BaseRoles, isSiteRole, RoleVariantEnum, UserSubType } from 'common/graphql/types';
import { useIsClinicalTrialLeadUser } from 'common/hooks/useIsClinicalTrialLeadUser';
import { OfflineManager } from 'common/native-app-support/native.offline.manager';
import { isSponsorUser, SPONSOR_HOME_PATH } from 'common/utils/sponsorUtils';
import { includes } from 'lodash';
import { isSecurityQuestionMissing, isSiteRoleBySubType } from 'module/auth/login/login.utils';
import { lazy, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { RootState } from 'store';

const LoginPage = lazy(() => import('module/auth/login/login.page'));

const STUDY_ROLES = [
    RoleVariantEnum.StudyBuilder,
    RoleVariantEnum.StudySupport,
    RoleVariantEnum.ClinicalTrialLead,
    RoleVariantEnum.BlindedStudyManager,
    RoleVariantEnum.AdminReadOnly,
];

export const LandingPage = (): ReactElement => {
    const auth = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const { activeUser, activeStudySite } = auth || {};
    const isOktaLogin =
        featureFlags.fxIntegration &&
        activeUser?.userType !== AuthUserType.PATIENT &&
        activeUser?.authenticationMethod === 'OKTA';
    const isClinicalTrialLeadUser = useIsClinicalTrialLeadUser();

    if (!activeUser) {
        return <LoginPage />;
    }

    if (isSecurityQuestionMissing(activeUser) && !isOktaLogin) {
        return <Redirect to="/activate" />;
    }

    if (activeUser.userType === AuthUserType.PATIENT) {
        return <Redirect to="/patientDashboard" />;
    }

    if (
        !OfflineManager.getInstance().isPhoneOffline() &&
        activeUser.currentPolicy?.[0] &&
        activeUser.acceptedVersionPP?.toString() !== activeUser.currentPolicy[0].majorVersion.toString() &&
        includes(activeUser.permissions, 'SubscriberPP_Read') // When updating permission here make sure to update in switch user
    ) {
        return <Redirect to="/policy" />;
    }

    if (
        featureFlags.fxIntegration &&
        !auth.activeUser.currentUserSubscriber?.currentRoleId &&
        auth.activeUser.currentUserSubscriber?.roleClaims?.length
    ) {
        return <Redirect to="/selectRoleView" />;
    }

    if (
        isSiteRoleBySubType(activeUser?.subType) ||
        // TODO: site coordinator had no subtype in recent changes, added next line. Remove the below line if BE starts to return subtype again.
        // Remove the above line if subtype becomes deprecated
        isSiteRole(activeUser?.roleVClaim?.sk) ||
        (!featureFlags.fxIntegration && activeUser.subType === UserSubType.PRODUCT_SUPPORT) ||
        isClinicalTrialLeadUser
    ) {
        if (
            !activeStudySite.activeStudySiteReady &&
            !!activeUser?.userStudies?.length &&
            !includes(location.search, 'studyId')
        ) {
            return <Redirect to="/activestudysite" />;
        }
        const homePath = isSponsorUser(activeUser) ? SPONSOR_HOME_PATH : `/siteDashboard${location.search}`;

        return <Redirect to={homePath} />;
    }

    const isStudyRole = includes(STUDY_ROLES, activeUser?.roleVClaim?.sk);
    if (
        (BaseRoles.AdminId === activeUser?.roleBClaim?.sk && !includes(activeUser.permissions, 'Subscriber_Create')) ||
        isStudyRole ||
        (featureFlags.fxIntegration && RoleVariantEnum.ProductSupport === activeUser?.roleVClaim?.sk)
    ) {
        const queryParams = new URLSearchParams(location.search);
        const redirectURL = queryParams?.get('studyId')
            ? `/studydetails/Study.${queryParams?.get('studyId')}`
            : '/study';

        return <Redirect to={redirectURL} />;
    }

    // since the subscriber admin and sys admin share the same base role
    if (activeUser?.role === RoleVariantEnum.SystemAdministrator) {
        return <Redirect to="/subscriber" />;
    }

    if (RoleVariantEnum.Librarian === activeUser?.roleVClaim?.sk) {
        return <Redirect to="/activitylibrary" />;
    }

    if (RoleVariantEnum.TranslationRepresentative === activeUser?.roleVClaim?.sk) {
        return <Redirect to="/userprofile/general" />;
    }

    return <Redirect to="/dashboard" />;
};
